import React, { useMemo } from "react";

interface StakeholderMapProps {
  contacts: any[];
  onContactClick?: (contact: any) => void;
}

const StakeholderMap: React.FC<StakeholderMapProps> = ({
  contacts,
  onContactClick,
}) => {
  const [hoveredContact, setHoveredContact] = React.useState<any | null>(
    null
  );
  const [tooltipPosition, setTooltipPosition] = React.useState({
    x: 0,
    y: 0,
  });

  // Chart dimensions
  const width = 800;
  const height = 500;
  const padding = { top: 40, right: 40, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Filter contacts with required data
  const validContacts = useMemo(() => {
    return contacts.filter(
      (c) =>
        c.influence_level &&
        c.relationship_strength !== null &&
        c.relationship_strength !== undefined
    );
  }, [contacts]);

  // Convert influence level to Y position
  const getInfluenceY = (level: string): number => {
    switch (level) {
      case "high":
        return chartHeight * 0.15; // Near top
      case "medium":
        return chartHeight * 0.5; // Middle
      case "low":
        return chartHeight * 0.85; // Near bottom
      default:
        return chartHeight * 0.5;
    }
  };

  // Get color based on role type
  const getRoleColor = (roleType?: string): string => {
    const colors: Record<string, string> = {
      decision_maker: "#dc2626", // red-600
      champion: "#16a34a", // green-600
      influencer: "#2563eb", // blue-600
      blocker: "#ea580c", // orange-600
      technical_evaluator: "#7c3aed", // violet-600
      budget_holder: "#ca8a04", // yellow-600
      end_user: "#64748b", // slate-500
    };
    return colors[roleType || ""] || "#94a3b8"; // slate-400 default
  };

  // Get role label
  const getRoleLabel = (roleType?: string): string => {
    if (!roleType) return "Unknown";
    return roleType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleMouseMove = (
    e: React.MouseEvent<SVGCircleElement>,
    contact: any
  ) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (svg) {
      const rect = svg.getBoundingClientRect();
      setTooltipPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    setHoveredContact(contact);
  };

  if (validContacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-20 w-20 mb-4 text-blue-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
        <p className="text-xl font-semibold text-gray-900 mb-3">
          Add Stakeholder Data to View Map
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
          <p className="text-sm text-blue-900 mb-2">
            <strong>To see contacts on this map:</strong>
          </p>
          <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
            <li>Edit a contact from the List view</li>
            <li>Fill in the <strong>Stakeholder Information</strong> section:</li>
            <ul className="ml-4 mt-1 space-y-0.5 list-disc">
              <li>Role Type (e.g., Decision Maker, Champion)</li>
              <li>Influence Level (High/Medium/Low)</li>
              <li>Relationship Strength (0-100)</li>
            </ul>
            <li>Save the contact - it will appear on the map!</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <svg width={width} height={height} className="mx-auto">
        {/* Background grid */}
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="url(#grid)"
        />

        {/* Chart border */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="none"
          stroke="#d1d5db"
          strokeWidth="2"
        />

        {/* Y-axis labels */}
        <text
          x={padding.left - 15}
          y={padding.top + chartHeight * 0.15}
          textAnchor="end"
          className="text-sm fill-gray-600"
        >
          High
        </text>
        <text
          x={padding.left - 15}
          y={padding.top + chartHeight * 0.5}
          textAnchor="end"
          className="text-sm fill-gray-600"
        >
          Medium
        </text>
        <text
          x={padding.left - 15}
          y={padding.top + chartHeight * 0.85}
          textAnchor="end"
          className="text-sm fill-gray-600"
        >
          Low
        </text>

        {/* Y-axis title */}
        <text
          x={20}
          y={height / 2}
          textAnchor="middle"
          className="text-sm font-medium fill-gray-700"
          transform={`rotate(-90 20 ${height / 2})`}
        >
          Influence Level
        </text>

        {/* X-axis labels */}
        <text
          x={padding.left}
          y={height - 30}
          textAnchor="middle"
          className="text-xs fill-gray-600"
        >
          0
        </text>
        <text
          x={padding.left + chartWidth / 2}
          y={height - 30}
          textAnchor="middle"
          className="text-xs fill-gray-600"
        >
          50
        </text>
        <text
          x={padding.left + chartWidth}
          y={height - 30}
          textAnchor="middle"
          className="text-xs fill-gray-600"
        >
          100
        </text>

        {/* X-axis title */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          className="text-sm font-medium fill-gray-700"
        >
          Relationship Strength
        </text>

        {/* Quadrant labels (faded) */}
        <text
          x={padding.left + chartWidth * 0.25}
          y={padding.top + 20}
          textAnchor="middle"
          className="text-xs fill-gray-400 font-medium"
        >
          Nurture
        </text>
        <text
          x={padding.left + chartWidth * 0.75}
          y={padding.top + 20}
          textAnchor="middle"
          className="text-xs fill-gray-400 font-medium"
        >
          Champions
        </text>
        <text
          x={padding.left + chartWidth * 0.25}
          y={padding.top + chartHeight - 10}
          textAnchor="middle"
          className="text-xs fill-gray-400 font-medium"
        >
          Monitor
        </text>
        <text
          x={padding.left + chartWidth * 0.75}
          y={padding.top + chartHeight - 10}
          textAnchor="middle"
          className="text-xs fill-gray-400 font-medium"
        >
          Maintain
        </text>

        {/* Data points */}
        {validContacts.map((contact) => {
          const x =
            padding.left + (contact.relationship_strength! / 100) * chartWidth;
          const y = padding.top + getInfluenceY(contact.influence_level!);
          const color = getRoleColor(contact.role_type);

          return (
            <g key={contact.id}>
              <circle
                cx={x}
                cy={y}
                r="8"
                fill={color}
                stroke="white"
                strokeWidth="2"
                className="cursor-pointer hover:opacity-80 transition-opacity"
                onMouseMove={(e) => handleMouseMove(e, contact)}
                onMouseLeave={() => setHoveredContact(null)}
                onClick={() => onContactClick?.(contact)}
              />
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoveredContact && (
        <div
          className="absolute bg-white border border-gray-300 rounded-lg shadow-lg p-3 pointer-events-none z-10"
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y + 10,
            minWidth: "200px",
          }}
        >
          <div className="font-semibold text-sm">
            {hoveredContact.first_name} {hoveredContact.last_name}
          </div>
          {hoveredContact.title && (
            <div className="text-xs text-gray-600 mb-2">
              {hoveredContact.title}
            </div>
          )}
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Role:</span>
              <span className="font-medium">
                {getRoleLabel(hoveredContact.role_type)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Influence:</span>
              <span className="font-medium capitalize">
                {hoveredContact.influence_level}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Relationship:</span>
              <span className="font-medium">
                {hoveredContact.relationship_strength}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
        {[
          "decision_maker",
          "champion",
          "influencer",
          "blocker",
          "technical_evaluator",
          "budget_holder",
          "end_user",
        ].map((role) => (
          <div key={role} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full border-2 border-white"
              style={{ backgroundColor: getRoleColor(role) }}
            />
            <span className="text-gray-700">{getRoleLabel(role)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StakeholderMap;
